import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useGetGym } from '#api/gyms';
import { useGetLecture } from '#api/lectures';
import alt_image from '#assets/svg/alt_image.svg';
import { BottomPolicy, Button, Text } from '#atoms';
import { PATH } from '#const/path';
import usePayment from '#hooks/usePayment';
import { LocationModal } from '#molecules';
import { ConfirmationBottomSheet } from '#organisims';
import { useApplications } from '#providers/Applications';
import { useUserInfo } from '#providers/UserInfo';
import { $applicationData } from '#states/atoms/applicationState';
import { $confirmationFormState } from '#states/atoms/confirmationFormState';
import { PrevWithTitleTextTemplate, useProgress } from '#templates';
import sessionStorageManager from '#utils/sessionStorageManager';
import { getPureText } from '#utils/typeguard';
import {
  TextContainer,
  Container,
  FragmentContainer,
  CoachInformationContainer,
  CoachInformationItem,
  UserInformationConatiner,
  UserInformationItem,
  PaymentItem,
  AltImageBox,
  ItemContainer,
  GymLocationButton,
  ButtonContainer
} from './styles';

interface LocationState {
  gymId: string;
  lectureId: string;
}

export function ApplicationPayment() {
  const navigate = useNavigate();
  const state = useLocation();
  const prevRoute = `/expert/${state.state.data.myUrl}`;

  const { gymId, lectureId } = useRecoilValue($applicationData);
  const { userInfo } = useUserInfo();
  // @TODO: 꼭 깔끔하게 고칠것
  // if (!state) {
  //   return null;
  // }
  // const { gymId = null, lectureId = null } = state.data as LocationState;
  if (!gymId || !lectureId) {
    return null;
  }
  const { data: lectureData } = useGetLecture(lectureId);
  const { data: gymData } = useGetGym(gymId);
  const { requestPayment } = useApplications();
  const [{ quantity, gymName }] = useRecoilState($confirmationFormState);

  const [locationModalOpen, setLocationModalOpen] = useState<boolean>(false);

  const { currentApplication } = useApplications();
  const { initializePayment, paymentComplete } = usePayment({
    data: {
      coachId: lectureData?.coachId ?? '',
      lectureId,
      lectureName: getPureText(lectureData?.title ?? ''),
      userEmail: userInfo?.email ?? '',
      userName: userInfo?.name ?? '',
      userPhone: userInfo?.phone ?? '',
      amount: lectureData?.price ?? 0,
      count: quantity ?? 0,
      gymId: gymId
    }
  });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (paymentComplete) {
      queryClient.invalidateQueries(['LECTURESINGLIST']);
      navigate(PATH.PAYMENT_SUCCESS);
    }
  }, [paymentComplete]);

  const payLectureAndProgress = async () => {
    requestPayment({ lectureId, gymId, count: quantity ?? 0 }, result => {
      result.merchant_uid && initializePayment(result.merchant_uid);
    });
  };

  return (
    <PrevWithTitleTextTemplate prevRoute={prevRoute} title={'결제 하기'}>
      <Container>
        <FragmentContainer className="class-info">
          <TextContainer>
            <Text textStyle="t3">신청 수업 정보</Text>
          </TextContainer>
          <CoachInformationContainer>
            <CoachInformationItem style={{ display: 'flex' }}>
              {lectureData?.coachProfile[0]?.resizedLink ? (
                <img src={lectureData.coachProfile[0].resizedLink} />
              ) : (
                <AltImageBox>
                  <img src={alt_image} width={26} height={26} />
                </AltImageBox>
              )}
              <ItemContainer>
                <Text>{lectureData?.coachName} 전문가</Text>
                <Text
                  textStyle="c1"
                  textColor="gray2"
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {lectureData?.title}
                </Text>
              </ItemContainer>
            </CoachInformationItem>
            <CoachInformationItem>
              <ItemContainer className="gym">
                <Text>{gymData?.name}</Text>
                <GymLocationButton onClick={() => setLocationModalOpen(true)}>
                  위치 보기
                </GymLocationButton>
              </ItemContainer>
            </CoachInformationItem>
            {currentApplication?.schedules &&
              currentApplication?.schedules.length > 0 && (
                <CoachInformationItem style={{ flexDirection: 'column' }}>
                  {currentApplication.schedules.map((date, idx) => {
                    const selectedDate = dayjs(date);
                    const formattedDate = `${
                      selectedDate.month() + 1
                    }월 ${selectedDate.date()}일 ${selectedDate
                      .locale('ko')
                      .format('dddd')} ${selectedDate.format(
                      'HH'
                    )}:00 - ${selectedDate.add(1, 'hour').format('HH')}:00`;
                    return <Text key={idx}>{formattedDate}</Text>;
                  })}
                </CoachInformationItem>
              )}
          </CoachInformationContainer>
        </FragmentContainer>
        <hr />
        <FragmentContainer>
          <TextContainer>
            <Text textStyle="t3">고객 정보 확인</Text>
          </TextContainer>
          <UserInformationConatiner>
            <UserInformationItem>
              <Text textStyle="t3">이름</Text>
              <Text>{userInfo?.name}</Text>
            </UserInformationItem>
            <UserInformationItem>
              <Text textStyle="t3">핸드폰 번호</Text>
              <Text>{userInfo?.phone}</Text>
            </UserInformationItem>
          </UserInformationConatiner>
        </FragmentContainer>
        <hr />
        <FragmentContainer>
          <TextContainer>
            <Text textStyle="t3">결제 금액</Text>
          </TextContainer>
          <PaymentItem>
            <div>
              <Text textStyle="c1" textColor="gray2">
                수업 정보
              </Text>
              <Text style={{ whiteSpace: 'pre-wrap' }}>
                {lectureData?.title}
              </Text>
            </div>
            <div>
              <Text textStyle="c1" textColor="gray2">
                회차정보
              </Text>
              <Text>{quantity}회</Text>
            </div>
          </PaymentItem>
          <PaymentItem>
            <div>
              <Text textStyle="c1">총 결제 금액</Text>
              <Text>
                {((lectureData?.price ?? 0) * (quantity ?? 0)).toLocaleString(
                  'ko-KR'
                )}
                원
              </Text>
            </div>
          </PaymentItem>
        </FragmentContainer>
        <BottomPolicy />
        <ButtonContainer>
          <Button onClick={payLectureAndProgress}>결제하기</Button>
        </ButtonContainer>
        {gymData && locationModalOpen && (
          <LocationModal
            data={gymData}
            onCancel={() => {
              setLocationModalOpen(false);
            }}
          />
        )}
      </Container>
    </PrevWithTitleTextTemplate>
  );
}
